import { useRef } from 'react';
import { ChatProvider } from '../contexts/ChatContext';
import { useChat } from '../hooks/useChat';
import { type PartialDesignBrief } from '../types/briefSchema';
import { ChatInputArea } from './ChatInputArea';
import { ChatMessageList } from './ChatMessageList';
import type { PriceType } from '@99designs/pricing';
import type { Message } from '../types/chat';
import type { ResizableTextAreaRef } from './ResizableTextArea';

export interface ChatInterfaceProps {
    designBrief: PartialDesignBrief;
    onBriefUpdate: (brief: PartialDesignBrief) => void;
    /** Called when the user clicks "Fill out a form instead". When undefined the prompt is hidden. */
    onSwitchToForm?: () => void;
    /** Server-side chat history used to hydrate the conversation on mount. */
    initialMessages?: Message[];
    initialBriefComplete?: boolean;
    onChatStarted?: () => void;
    /** Called when the user clicks "Review your brief" on the completion card */
    onReview: () => void;
    /** Called when the user clicks a CTA link in a chat message. */
    onCtaLinkClick?: (url: string, label: string) => void;
    printProductMpvId: string;
    initialMaxMessages?: number | null;
    /** correlationId from the brief request; used to fire ServiceOptionsUpdated events. */
    requestId?: string;
    serviceProductKey?: string;
    serviceProductVersion?: number;
    serviceProductOptions?: Array<{ key: string; value: string }>;
    productName?: string;
    productPrice?: PriceType;
}

export function ChatInterface({
    designBrief,
    onBriefUpdate,
    onSwitchToForm,
    initialMessages,
    initialBriefComplete,
    onChatStarted,
    onReview,
    onCtaLinkClick,
    printProductMpvId,
    initialMaxMessages,
    requestId,
    serviceProductKey,
    serviceProductVersion,
    serviceProductOptions,
    productName,
    productPrice,
}: ChatInterfaceProps) {
    const chat = useChat({
        designBrief,
        onBriefUpdate,
        initialMessages,
        initialBriefComplete,
        onChatStarted,
        initialMaxMessages,
        requestId,
        serviceProductKey,
        serviceProductVersion,
        serviceProductOptions,
    });

    // Shared s.t. the message list's Escape handler can move focus back to the composer.

    const inputRef = useRef<ResizableTextAreaRef>(null);

    return (
        <ChatProvider
            value={chat}
            printProductMpvId={printProductMpvId}
            designBrief={designBrief}
            requestId={requestId}
            productName={productName}
            productPrice={productPrice}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    // Fill the parent container. ChatBrief wraps this in a
                    // viewport-height flex column alongside MultiStepTopBar, so
                    // the chat naturally takes the remaining space below the
                    // top bar (and below the site bookends header above it).
                    height: '100%',
                    minHeight: 0,
                }}
            >
                <ChatMessageList
                    onReview={onReview}
                    onCtaLinkClick={onCtaLinkClick}
                    focusInput={() => inputRef.current?.focus()}
                />
                <ChatInputArea ref={inputRef} onSwitchToForm={onSwitchToForm} onReview={onReview} />
            </div>
        </ChatProvider>
    );
}
